import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Calculator, PenTool, Check, Lock, Sparkles, Heart, ShieldCheck, Clock, Loader2,
} from 'lucide-react';
import BrandMark from '@/components/BrandMark';
import { hasFullAccess } from '@/utils/entitlements';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const YEAR_PASS_PRICE_NZD = '$99.99'; // GST-inclusive. LS variant base price is $86.95 + 15% NZ GST
const WEEKLY_PRICE_NZD = '$3.99'; // GST-inclusive. LS variant base price is $3.47 + 15% NZ GST
const WEEKLY_FULL_YEAR_ESTIMATE_NZD = '$159.60'; // 40 weeks × $3.99
const SUBJECTS = [
  { key: 'maths', label: 'Pāngarau / Maths', Icon: Calculator, accent: 'moana' },
  { key: 'english', label: 'Reo Pākehā / English', Icon: PenTool, accent: 'fern' },
];

const VALUE_PROPS = [
  {
    icon: Heart,
    title: 'Try 4 lessons free',
    body: 'Every account gets the first 2 lessons of Maths AND English unlocked from day one. No credit card required to start.',
  },
  {
    icon: ShieldCheck,
    title: 'Pay once. Yours forever.',
    body: 'No subscriptions. No recurring charges. One payment unlocks every lesson in that year for that child × subject.',
  },
  {
    icon: Sparkles,
    title: 'Two subjects, your pace',
    body: 'Pick Pāngarau, Reo Pākehā, or both. Each tamaiti has their own progress, planner and certificates.',
  },
];

export default function PricingPage() {
  const { user, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [entitlements, setEntitlements] = useState([]);
  const [unlimitedAccess, setUnlimitedAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null); // `${child}-${subject}` while creating LS checkout

  useEffect(() => {
    const load = async () => {
      if (!user) { setLoading(false); return; }
      try {
        const r = await axios.get(`${API}/billing/entitlements`, { headers: authHeaders });
        setEntitlements(r.data.entitlements || []);
        setUnlimitedAccess(!!r.data.unlimited_access);
      } catch (e) { /* not signed in */ }
      setLoading(false);
    };
    load();
  }, [user, authHeaders]);

  const isOwned = (childName, subject, yearLevel) => {
    const childEntitlements = entitlements.filter(e => e.child_name === childName);
    return hasFullAccess(childEntitlements, subject, unlimitedAccess, yearLevel);
  };

  const handleBuy = async (childName, subject, plan, yearLevel) => {
    if (!user) {
      navigate('/auth');
      return;
    }
    const key = `${childName}-${subject}-${plan}`;
    setPurchasing(key);
    try {
      const r = await axios.post(
        `${API}/billing/checkout`,
        { child_name: childName, subject, plan, year_level: yearLevel },
        { headers: authHeaders }
      );
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout. Please try again.');
      setPurchasing(null);
    }
  };

  const children = user?.children || [];

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="pricing-page">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-white/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center"><BrandMark size={36} /></Link>
          <Link to={user ? '/' : '/auth'} data-testid="pricing-back">
            <Button variant="ghost" className="text-slate-600 hover:text-fern hover:bg-fern/5 rounded-lg gap-2">
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">{user ? 'Dashboard' : 'Back'}</span>
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        {/* Hero */}
        <div className="text-center mb-14">
          <Badge className="bg-kowhai/30 text-fern-dark border-0 rounded-full px-3 py-1 text-xs uppercase tracking-[0.18em] font-semibold mb-5">
            Simple, fair pricing
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-fern-dark tracking-tight">
            Pay once. <span className="text-moana">Yours forever.</span>
          </h1>
          <p className="text-slate-600 text-lg mt-4 max-w-2xl mx-auto leading-relaxed">
            Start with <strong className="text-fern-dark">4 free lessons</strong>: Maths Day 1, Maths Day 2, English Day 1, English Day 2. Loved them?
            Unlock week by week, or pay once per child, per subject for everything. No subscriptions, ever.
          </p>
          <div className="mt-6 inline-flex flex-wrap justify-center items-baseline gap-x-6 gap-y-2 bg-white border border-fern/10 rounded-full px-6 py-3 shadow-sm">
            <span className="flex items-baseline gap-2">
              <span className="text-slate-500 text-sm">NZD</span>
              <span className="font-heading text-4xl font-bold text-fern-dark">{YEAR_PASS_PRICE_NZD}</span>
              <span className="text-slate-500 text-sm">/ child / subject</span>
            </span>
            <span className="text-slate-300">or</span>
            <span className="flex items-baseline gap-2">
              <span className="font-heading text-2xl font-bold text-fern-dark">{WEEKLY_PRICE_NZD}</span>
              <span className="text-slate-500 text-sm">/ week</span>
            </span>
          </div>
        </div>

        {/* Value props */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14">
          {VALUE_PROPS.map(({ icon: Icon, title, body }) => (
            <Card key={title} className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
              <CardContent className="p-6">
                <div className="w-11 h-11 rounded-2xl bg-fern/10 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-fern" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-fern-dark mb-1">{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Buy matrix */}
        <Card data-testid="pricing-matrix" className="border-0 shadow-[0_4px_24px_-4px_rgba(0,0,0,0.06)] rounded-3xl overflow-hidden">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
              <div>
                <h2 className="font-heading text-2xl font-semibold text-fern-dark">
                  {user ? 'Choose what to unlock' : 'What you get'}
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  {user
                    ? 'Buy access for each child × subject combo. Mix and match.'
                    : 'Sign in or join, then come back here to make a purchase.'}
                </p>
              </div>
              {unlimitedAccess && (
                <Badge className="bg-fern text-white border-0 rounded-full px-3 py-1">
                  All access (admin)
                </Badge>
              )}
            </div>

            {!user ? (
              <div className="rounded-2xl border border-fern/15 bg-fern/5 p-8 text-center">
                <p className="text-slate-600 mb-4">Create your free account first. It takes 30 seconds.</p>
                <Link to="/auth">
                  <Button data-testid="pricing-signup-cta" className="bg-fern hover:bg-fern-light text-white rounded-full px-6">
                    Create Account
                  </Button>
                </Link>
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center py-12 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your access…
              </div>
            ) : children.length === 0 ? (
              <div className="rounded-2xl border border-fern/15 bg-fern/5 p-8 text-center">
                <p className="text-slate-600 mb-4">Add a child to your whānau first.</p>
                <Link to="/family"><Button className="bg-fern hover:bg-fern-light text-white rounded-full">Manage Family</Button></Link>
              </div>
            ) : (
              <div className="space-y-4">
                {children.map((child) => (
                  <div key={child.name} data-testid={`pricing-child-${child.name}`} className="rounded-2xl border border-slate-100 bg-slate-50/40 p-4 sm:p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-9 h-9 rounded-full bg-fern text-white flex items-center justify-center font-semibold">
                        {child.name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="font-heading text-lg font-semibold text-fern-dark">{child.name}</p>
                        <p className="text-xs text-slate-500">Year {child.year_level}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {SUBJECTS.map(({ key, label, Icon, accent }) => {
                        const owned = isOwned(child.name, key, child.year_level);
                        return (
                          <div
                            key={key}
                            data-testid={`pricing-tile-${child.name}-${key}`}
                            className={`rounded-2xl border p-4 transition-all ${
                              owned ? 'border-fern/20 bg-fern/5' : 'border-slate-200 bg-white hover:border-fern/30 hover:shadow-md'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${accent === 'moana' ? 'bg-moana-50 text-moana' : 'bg-fern-50 text-fern'}`}>
                                  <Icon className="w-4 h-4" />
                                </div>
                                <p className="font-medium text-fern-dark text-sm">{label}</p>
                              </div>
                              {owned ? (
                                <Badge className="bg-fern text-white border-0 rounded-full text-[10px] px-2 py-0.5 gap-1">
                                  <Check className="w-3 h-3" /> Unlocked
                                </Badge>
                              ) : (
                                <span className="text-xs text-slate-400 flex items-center gap-1">
                                  <Lock className="w-3 h-3" /> Locked
                                </span>
                              )}
                            </div>
                            {owned ? (
                              <p className="text-xs text-slate-500">
                                {unlimitedAccess ? 'Admin access. Every lesson unlocked.' : 'Full access unlocked.'}
                              </p>
                            ) : (
                              <div className="space-y-2">
                                <Button
                                  data-testid={`pricing-buy-weekly-${child.name}-${key}`}
                                  onClick={() => handleBuy(child.name, key, 'weekly', child.year_level)}
                                  disabled={purchasing === `${child.name}-${key}-weekly` || purchasing === `${child.name}-${key}-year_pass`}
                                  variant="outline"
                                  className="w-full rounded-full"
                                >
                                  {purchasing === `${child.name}-${key}-weekly`
                                    ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Opening checkout…</>
                                    : <>Unlock next week, {WEEKLY_PRICE_NZD}</>}
                                </Button>
                                <Button
                                  data-testid={`pricing-buy-yearpass-${child.name}-${key}`}
                                  onClick={() => handleBuy(child.name, key, 'year_pass', child.year_level)}
                                  disabled={purchasing === `${child.name}-${key}-weekly` || purchasing === `${child.name}-${key}-year_pass`}
                                  className={`w-full rounded-full text-white ${accent === 'moana' ? 'bg-moana hover:bg-moana-light' : 'bg-fern hover:bg-fern-light'}`}
                                >
                                  {purchasing === `${child.name}-${key}-year_pass`
                                    ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Opening checkout…</>
                                    : <>Unlock everything, {YEAR_PASS_PRICE_NZD}</>}
                                </Button>
                                <p className="text-[11px] text-slate-400 text-center">
                                  A full year week-by-week costs ~{WEEKLY_FULL_YEAR_ESTIMATE_NZD}, so the Year Pass saves you money if you'll use it all year.
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* FAQ */}
        <div className="mt-14">
          <h2 className="font-heading text-2xl font-semibold text-fern-dark mb-5">Quick answers</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { q: 'Do I need to pay to try NestWise?', a: 'No. Every new account gets the first 2 lessons of Maths AND English unlocked for free \u2014 no credit card needed. Lessons 3 onwards require unlocking the subject for that child.' },
              { q: 'Is this a subscription?', a: 'No. It\u2019s a one-off payment per child per subject. No recurring charges, ever.' },
              { q: 'How long does access last?', a: 'For the lifetime of the product. There\u2019s no expiry on what you\u2019ve unlocked.' },
              { q: 'Do I need to buy both subjects?', a: 'Nope. Pick the subjects you actually want \u2014 Pāngarau, Reo Pākehā, or both.' },
              { q: 'What about siblings?', a: 'Each child has their own progress, planner and certificates, so each child × subject is a separate unlock. Each child also gets their own free trial.' },
              { q: 'How is payment handled?', a: 'Payments are processed securely by Lemon Squeezy, our payment provider. They handle the receipt and any tax obligations on the transaction.' },
            ].map(({ q, a }) => (
              <Card key={q} className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-2xl">
                <CardContent className="p-5">
                  <p className="font-medium text-fern-dark mb-1.5 flex items-start gap-2"><Clock className="w-4 h-4 text-fern mt-1 flex-shrink-0" /> {q}</p>
                  <p className="text-sm text-slate-600 leading-relaxed pl-6">{a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
